import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtUSD, formatApiError } from "../lib/api";
import { CheckCircle2, AlertCircle, ArrowRight, Package, Mail, Truck } from "lucide-react";
import { toast } from "sonner";

export default function OrderSuccessPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [order, setOrder] = useState(null);
    const [error, setError] = useState("");
    const [polls, setPolls] = useState(0);
    const [toasted, setToasted] = useState(false);
    // Read ?session_id (from Stripe redirect) once — force-reconciles the
    // order server-side even if the webhook is delayed.
    const sessionId = new URLSearchParams(window.location.search).get("session_id");

    useEffect(() => {
        let stop = false;
        const reconcileAndFetch = async () => {
            try {
                if (sessionId) {
                    try { await api.get(`/payments/status/${sessionId}`); } catch { /* fall through */ }
                }
                const { data } = await api.get(`/orders/${id}`);
                if (stop) return;
                setOrder(data);
                if (!toasted) {
                    toast.success("Payment received — your order has been placed!", { duration: 6000 });
                    setToasted(true);
                }
                if (data.status !== "paid" && polls < 8) {
                    setTimeout(() => setPolls((p) => p + 1), 3000);
                }
            } catch (e) {
                if (stop) return;
                setError(formatApiError(e));
            }
        };
        reconcileAndFetch();
        return () => { stop = true; };
    }, [id, polls, sessionId, toasted]);

    if (error) {
        return (
            <div data-testid="order-success-error" className="pt-52 pb-24 bg-[var(--bg)] min-h-screen text-center px-6">
                <AlertCircle size={48} className="text-[var(--error)] mx-auto mb-6" />
                <div className="data-label mb-3">UNABLE TO LOAD ORDER</div>
                <p className="text-secondary mb-6">{error}</p>
                <button onClick={() => navigate("/account/orders")} className="btn btn-gold">View My Orders</button>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="pt-52 text-center bg-[var(--bg)] min-h-screen">
                <p className="text-secondary">Loading order confirmation…</p>
            </div>
        );
    }

    const paid = order.status === "paid";
    const isCard = order.payment_method === "card";

    return (
        <div data-testid="order-success-page" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-3xl mx-auto px-6 lg:px-10">
                <div className="text-center mb-10">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[var(--brand-soft)] border-2 border-[var(--brand)] mx-auto mb-6 animate-in zoom-in-50 duration-500">
                        <CheckCircle2 size={40} className="text-brand" />
                    </div>
                    <div className="data-label text-brand mb-3">
                        {paid ? "PAYMENT CONFIRMED" : isCard ? "PAYMENT RECEIVED" : "ORDER RECEIVED"}
                    </div>
                    <h1 data-testid="order-success-headline" className="font-serif text-4xl md:text-5xl mb-4">
                        Thank you — your order has been placed.
                    </h1>
                    <p className="text-secondary max-w-xl mx-auto">
                        {isCard ? (
                            paid
                                ? <>Your card payment has been <span className="text-brand font-mono">CONFIRMED</span>. Your order is now being prepared and will ship within 24 hours.</>
                                : <>Your card transaction was completed successfully. We're finalizing the confirmation with your card processor — this typically clears within seconds. <span className="text-primary">No further action needed from you.</span></>
                        ) : (
                            paid
                                ? <>Your payment has been <span className="text-brand font-mono">CONFIRMED</span>. Your order will ship within 24 hours.</>
                                : <>Your order is recorded. Please complete the payment instructions sent to your email to start processing.</>
                        )}
                    </p>
                </div>

                <div className="card p-6 mb-6">
                    <div className="flex justify-between items-baseline mb-4">
                        <div className="data-label">ORDER NUMBER</div>
                        <div data-testid="order-number" className="font-mono text-brand text-lg">{order.order_number}</div>
                    </div>
                    <div className="hairline mb-4" />
                    <div className="flex justify-between items-baseline mb-4">
                        <span className="data-label">AMOUNT {isCard ? "CHARGED" : "DUE"}</span>
                        <span className="font-serif text-3xl text-brand">{fmtUSD(order.total)}</span>
                    </div>
                    <div className="hairline mb-4" />
                    <div className="flex justify-between items-baseline">
                        <span className="data-label">STATUS</span>
                        <span data-testid="order-status" className={`font-mono text-sm ${paid ? "text-brand" : "text-secondary"}`}>
                            {paid ? "PAID · PROCESSING" : isCard ? "PAYMENT CLEARING" : "AWAITING PAYMENT"}
                        </span>
                    </div>
                </div>

                <div className="card p-6 mb-6">
                    <div className="data-label text-brand mb-4">WHAT HAPPENS NEXT</div>
                    <div className="space-y-3">
                        <div className="flex items-start gap-3">
                            <Mail size={16} className="text-brand mt-0.5 shrink-0" />
                            <div className="font-mono text-xs text-secondary leading-relaxed">
                                A confirmation email is on its way to your inbox with your order details and receipt.
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Package size={16} className="text-brand mt-0.5 shrink-0" />
                            <div className="font-mono text-xs text-secondary leading-relaxed">
                                Our lab team will prepare your order and verify each vial against batch COA.
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <Truck size={16} className="text-brand mt-0.5 shrink-0" />
                            <div className="font-mono text-xs text-secondary leading-relaxed">
                                Your order ships within 24 hours of payment confirmation. Tracking will be emailed to you.
                            </div>
                        </div>
                    </div>
                </div>

                {order.items?.length > 0 && (
                    <div className="card p-6 mb-6">
                        <div className="data-label text-brand mb-4 flex items-center gap-2">
                            <Package size={14} /> ITEMS
                        </div>
                        <div className="space-y-3">
                            {order.items.map((it, idx) => (
                                <div key={idx} className="flex justify-between items-center font-mono text-sm">
                                    <span className="text-primary">{it.name} × {it.quantity}</span>
                                    <span className="text-secondary">{fmtUSD(it.line_total ?? (it.unit_price || 0) * it.quantity)}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <p className="text-sm text-secondary text-center mb-6">
                    {paid
                        ? "A confirmation email has been sent. You can track your order in your account at any time."
                        : "This page will update automatically once the payment confirms. Safe to leave — your order is secured."}
                </p>

                <div className="flex justify-center gap-3 flex-wrap">
                    <button onClick={() => navigate("/account/orders")} data-testid="view-orders-btn" className="btn">
                        View My Orders <ArrowRight size={14} />
                    </button>
                    <button onClick={() => navigate("/shop")} data-testid="continue-browsing-btn" className="btn btn-gold">
                        Continue Browsing
                    </button>
                </div>
            </div>
        </div>
    );
}
